import React from 'react';
import Immutable from 'immutable';
import {Trans} from '@lingui/macro';
import {useDayzed} from 'dayzed';
import isSunday from 'date-fns/isSunday';
import isSaturday from 'date-fns/isSaturday';
import {useSelector} from 'react-redux';

import Button from '@components/Button';
import FlatButton from '@components/FlatButton';
import ClockIcon from '@components/Icons/ClockIcon';
import ChevronRightIcon from '@components/Icons/ChevronRightIcon';
import CompletedIcon from '@components/Icons/CompletedIcon';

import useMediaQuery from '@hooks/useMediaQuery';
import {
  extractTranslatedLabel,
  formatDate,
  weekdayNames,
  weekdayNamesShort,
} from '@services/utilities';

import merge from 'classnames';
import s from './Calendar.module.css';

export default function Calendar({tests, selectedTest, onSelectTest, position}) {
  const lang = useSelector(state => state.getIn(['userCampaign', 'data', 'lang']));
  const isMobile = useMediaQuery('(max-width: 600px)');
  const {calendars, getDateProps} = useDayzed({
    showOutsideDays: true,
    date: new Date('01 November 2021'),
  });

  const country = lang.split('_')[0].toLowerCase();

  return (
    <>
      <div className={s.CalendarContainer}>
        {calendars.map(calendar => (
          <div key={`${calendar.month}${calendar.year}`} className={s.WeekdayName}>
            <div className={merge(s.CalendarGrid, s.WeekDayNameContainer)}>
              {(isMobile ? weekdayNamesShort[country] : weekdayNames[country]).map(weekday => (
                <div key={`${calendar.month}${calendar.year}${weekday}`} className={s.WeekDayName}>
                  {weekday}
                </div>
              ))}
            </div>

            {calendars?.[0].weeks.map((week, weekIndex) => (
              <div key={weekIndex} className={merge(s.CalendarGrid, s.Week)}>
                {week.map((dateObj, dateIndex) => {
                  const {date} = dateObj;
                  const key = `${calendar.month}${calendar.year}${weekIndex}${dateIndex}`;

                  const currDateTest = tests?.find(test => {
                    const testDate = test.get('startDate');
                    const currDate = formatDate(date, 'dd/MM/yyyy');

                    return currDate === testDate;
                  });

                  const isPositionOnToday = position === formatDate(date, 'dd/MM/yyyy');

                  return date ? (
                    <DayBox
                      key={key}
                      className={s.Day}
                      date={date}
                      test={currDateTest}
                      tests={tests}
                      isPositionOnToday={isPositionOnToday}
                      selectedTest={selectedTest}
                      onSelectTest={onSelectTest}
                      {...getDateProps({dateObj})}
                    />
                  ) : (
                    <div key={key} className={s.Day} />
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

function DayBox(props) {
  const lang = useSelector(state => state.getIn(['userCampaign', 'data', 'lang']));
  const isMobile = useMediaQuery('(max-width: 600px)');
  const {
    key,
    className,
    date,
    test,
    tests,
    selectedTest,
    onSelectTest,
    dateProps,
    isPositionOnToday,
  } = props;

  const isTestSelected = test?.get('id') === selectedTest;
  const prerequisites = test?.getIn(['jsonData', 'prerequisites'], []);
  const hasPrerequisites = prerequisites?.size > 0;

  //TO REMOVE LATER ON:
  //CHATBOT IS NOW IN GHOST AND DISABLED
  const showTestInGhost = hasPrerequisites && !getArePrerequisitesCompleted(tests, prerequisites);

  if (isMobile) {
    const isLight = isSunday(date) || isSaturday(date);

    if (!test?.size) {
      return (
        <div key={key} className={merge(className, s.DayBox, isLight && s.Light)} {...dateProps}>
          <span className={s.DayNumber}>{date.getDate()}</span>
        </div>
      );
    }

    return (
      <FlatButton
        key={key}
        className={merge(
          className,
          s.DayBox,
          isPositionOnToday && s.Selected,
          isTestSelected && s.SelectedTest,
          showTestInGhost && s.MobileDisabled,
          test?.size > 0 && s.MobileTest,
          isLight && s.Light,
          'sk-calendar-daybox',
          showTestInGhost && 'sk-calendar-prerequisite'
        )}
        disabled={showTestInGhost}
        onClick={() => {
          if (!showTestInGhost) {
            onSelectTest(test?.get('id'));
          }
        }}
        {...dateProps}
      >
        {test.get('status') === 'complete' ? (
          <>
            <div className={s.CompletedIconMobile}>
              <CompletedIcon />
            </div>
          </>
        ) : null}
        {isPositionOnToday && <ChevronRightIcon className={s.ChevronLeft} />}
        <span className={s.DayNumber}>{date.getDate()}</span>
        {isPositionOnToday && (
          <ChevronRightIcon className={s.ChevronRight} style={{transform: 'rotate(180deg)'}} />
        )}
      </FlatButton>
    );
  }

  return (
    <div
      key={key}
      className={merge(
        className,
        s.DayBox,
        isPositionOnToday && s.Selected,
        isTestSelected && s.SelectedTest,
        test?.size > 0 && 'sk-calendar-daybox',
        showTestInGhost && 'sk-calendar-prerequisite'
      )}
      {...dateProps}
    >
      <span className={s.DayNumber}>{date.getDate()}</span>

      {test?.size > 0 && (
        <Button
          className={merge(s.TestBox, showTestInGhost && s.Disabled)}
          onClick={() => onSelectTest(test?.get('id'))}
          disabled={showTestInGhost}
        >
          <div className={s.TestSubtitle}>
            {test.get('status') === 'complete' ? (
              <>
                <div className={s.CompletedIcon}>
                  <CompletedIcon />
                </div>
                <div>
                  <Trans id="calendar.complete">Completato</Trans>
                </div>
              </>
            ) : (
              <>
                <ClockIcon currentColor={isTestSelected ? '#fff' : undefined} />
                <div>{test?.getIn(['jsonData', 'theme', 'duration']) || '09.00 - 18.00 PM'}</div>
              </>
            )}
          </div>
          <div className={s.Title}>
            {test?.get('name') ? extractTranslatedLabel(test, lang, 'name') : null}
          </div>
          <div className={s.Tag}>{test?.getIn(['jsonData', 'theme', 'tag'])}</div>
        </Button>
      )}
    </div>
  );
}

function getArePrerequisitesCompleted(tests, prerequisites) {
  if (!tests) {
    return false;
  }

  if (!prerequisites || prerequisites.size === 0) {
    return true;
  }

  const completedTestsIds = tests
    .filter(test => test.get('status') === 'complete')
    .map(test => Number(test.get('id')));
  const prerequisitesIds = Immutable.Set(prerequisites.map(test => Number(test)));
  const missingTests = prerequisitesIds.subtract(completedTestsIds);

  if (missingTests.size === 0) {
    return true;
  }

  return false;
}
